import { arrangeCollage } from '@data/home-collage';
import Link from 'next/link';
import styled, { keyframes } from 'styled-components';
import Image from '@/components/image';
import { up } from '@/themes';
import { getFallbackImage } from '@/utils/images';
import { brandHref } from './brand-href';

/*
 * Where each card sits, in % of the collage box (left, top, width), with its
 * tilt in degrees and stacking order. Slot numbers are shared with the share
 * images (data/home-collage.js): 0 is the centre card, 4 the one at the back,
 * behind the centre card and the small one in front (5).
 */
const SLOTS = [
  { left: 26, top: 35, width: 48, rotate: -3, z: 5 },
  { left: 0, top: 9, width: 40, rotate: -8, z: 2 },
  { left: 57, top: 4, width: 42, rotate: 7, z: 3 },
  { left: 2, top: 63, width: 38, rotate: 6, z: 3 },
  { left: 60, top: 59, width: 40, rotate: -6, z: 1 },
  { left: 33, top: 76, width: 34, rotate: 3, z: 6 },
  { left: 36, top: 0, width: 25, rotate: -2, z: 1 },
];

const float = keyframes`
  from {
    transform: translate3d(0, -5px, 0);
  }

  to {
    transform: translate3d(0, 5px, 0);
  }
`;

const List = styled.ul`
  aspect-ratio: 10 / 9;
  /* Keeps the cards' stacking order to themselves (the search list goes over them). */
  isolation: isolate;
  list-style: none;
  margin: 0 auto;
  max-width: 560px;
  padding: 0;
  position: relative;
  width: 100%;
`;

const Slot = styled.li`
  left: ${({ $slot }) => $slot.left}%;
  position: absolute;
  top: ${({ $slot }) => $slot.top}%;
  width: ${({ $slot }) => $slot.width}%;
  z-index: ${({ $slot }) => $slot.z};

  &:hover,
  &:focus-within {
    z-index: 10;
  }

  @media (prefers-reduced-motion: no-preference) {
    animation: ${float} ${({ $index }) => 5 + ($index % 3)}s ease-in-out
      ${({ $index }) => $index * -1.3}s infinite alternate;
  }
`;

const CardLink = styled(Link)`
  align-items: center;
  aspect-ratio: 5 / 3;
  /* A brand's own backing colour: the same in both themes. */
  background-color: ${({ $backgroundColor }) => $backgroundColor || '#ffffff'};
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-xl);
  display: flex;
  justify-content: center;
  overflow: hidden;
  padding: 10%;

  ${up.sm} {
    border-radius: var(--radius-2xl);
  }
  transform: rotate(${({ $rotate }) => $rotate}deg);
  transition:
    transform 300ms cubic-bezier(0.22, 1, 0.36, 1),
    box-shadow 300ms;

  &:hover,
  &:focus-visible {
    transform: rotate(0deg) scale(1.06);
  }

  &:focus-visible {
    outline: 2px solid var(--ring);
    outline-offset: 3px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  picture {
    align-items: center;
    display: flex;
    height: 100%;
    justify-content: center;
    width: 100%;
  }

  img {
    height: auto;
    max-height: 100%;
    max-width: 100%;
    object-fit: contain;
    width: auto;
  }
`;

/**
 * Hero visual: a loose stack of tilted, gently floating brand cards, each
 * linking to its brand page. Purely CSS, so it is identical on the server.
 */
const BrandCollage = ({ brands = [], className, label }) => {
  // Pinned brands take their slot (data/featured-brands.js); the rest fill in order.
  const cards = arrangeCollage(brands, SLOTS.length);

  if (cards.length === 0) return null;

  return (
    <List aria-label={label} className={className}>
      {cards.map((brand, index) => (
        <Slot $index={index} $slot={SLOTS[index]} key={brand.slug}>
          <CardLink
            $backgroundColor={brand.backgroundColor}
            $rotate={SLOTS[index].rotate}
            href={brandHref(brand)}
          >
            <Image
              alt={brand.name}
              fallback={getFallbackImage('giftcard')}
              src={brand.imageUrl}
              unoptimized
            />
          </CardLink>
        </Slot>
      ))}
    </List>
  );
};

export default BrandCollage;
